import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ZuzuLogo from "../assets/zuzu-logo.png";
import { isNative, showRewardedAndWait, initAd } from "../lib/ad";

/* ===============================
   Config & Constants
=============================== */
type Sym = "BTCUSDT" | "ETHUSDT" | "SOLUSDT" | "XRPUSDT";
const SYMBOLS: Sym[] = ["BTCUSDT", "ETHUSDT", "SOLUSDT", "XRPUSDT"];

const FETCH_MS = 10_000;
const FREE_GRANT_MS = 2 * 60 * 60 * 1000;
const IDX_1H = 60;
const IDX_4H = 240;
const IDX_24H = 1440;
const IGNORE_DELTA = 0.0007;

const KLINE_API = (sym: string) =>
  `https://api.binance.com/api/v3/klines?symbol=${sym}&interval=1m&limit=1440`;
const TICKER_API = "https://api.binance.com/api/v3/ticker/24hr";

const SS = { BASIC_UNTIL: "signal.basicUntil" };

/* ===============================
   Utils
=============================== */
const calcROI = (arr: number[], idx: number) => {
  if (!arr || arr.length <= idx) return null;
  const base = arr[arr.length - idx - 1];
  const last = arr[arr.length - 1];
  return ((last - base) / base) * 100;
};

/* ===============================
   Sparkline
=============================== */
const Sparkline = React.memo(
  ({ data, up }: { data: number[]; up: boolean }) => {
    if (data.length < 2) return <div className="spark" />;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    const points = data
      .map((v, i) => {
        const x = (i / (data.length - 1)) * 100;
        const y = 100 - ((v - min) / range) * 100;
        return `${x},${y}`;
      })
      .join(" ");

    return (
      <svg viewBox="0 0 100 100" className="spark" preserveAspectRatio="none">
        <polyline
          points={points}
          fill="none"
          stroke={up ? "#4ade80" : "#f87171"}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.85"
        />
      </svg>
    );
  }
);

/* ===============================
   Page
=============================== */
const SignalIntroPage: React.FC = () => {
  const { t } = useTranslation("signalIntro");
  const nav = useNavigate();

  const [busy, setBusy] = useState(false);
  const [remainMin, setRemainMin] = useState(0);
  const [spark, setSpark] = useState<Record<string, number[]>>({});
  const [tickers, setTickers] = useState<Record<string, any>>({});

  useEffect(() => {
    if (isNative()) initAd();
  }, []);

  useEffect(() => {
    const update = () => {
      const until = Number(localStorage.getItem(SS.BASIC_UNTIL) || 0);
      setRemainMin(Math.max(0, Math.floor((until - Date.now()) / 60000)));
    };
    update();
    const id = setInterval(update, 10_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const s: any = {};
      const tks: any = {};
      await Promise.all(
        SYMBOLS.map(async (sym) => {
          try {
            const r = await fetch(KLINE_API(sym));
            const arr = await r.json();
            if (alive && Array.isArray(arr)) {
              const prices = arr.map((k: any) => Number(k[4]));
              s[sym] = prices;
              tks[sym] = {
                price: prices.at(-1),
                roi1h: calcROI(prices, IDX_1H),
                roi4h: calcROI(prices, IDX_4H),
                roi24h: calcROI(prices, IDX_24H),
              };
            }
          } catch {}
        })
      );
      if (alive) {
        setSpark(s);
        setTickers(tks);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const r = await fetch(TICKER_API);
        const arr = await r.json();
        if (!Array.isArray(arr)) return;

        setSpark((prev) => {
          const next = { ...prev };
          const nextTickers: any = {};

          SYMBOLS.forEach((s) => {
            const row = arr.find((x: any) => x.symbol === s);
            if (!row) return;

            const price = Number(row.lastPrice);
            const history = next[s] || [];
            const last = history.at(-1);

            if (!last || Math.abs((price - last) / last) >= IGNORE_DELTA) {
              next[s] = [...history, price].slice(-IDX_24H);
            }

            const h = next[s];
            nextTickers[s] = {
              price,
              roi1h: calcROI(h, IDX_1H),
              roi4h: calcROI(h, IDX_4H),
              roi24h: calcROI(h, IDX_24H),
            };
          });

          setTickers(nextTickers);
          return next;
        });
      } catch {}
    }, FETCH_MS);

    return () => clearInterval(id);
  }, []);

  const enter = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const until = Number(localStorage.getItem(SS.BASIC_UNTIL) || 0);
      if (Date.now() < until) {
        nav("/signal-bot", { replace: true });
        return;
      }

      if (isNative()) {
        await Promise.race([
          showRewardedAndWait(),
          new Promise((r) => setTimeout(r, 10_000)),
        ]);
      }

      localStorage.setItem(SS.BASIC_UNTIL, String(Date.now() + FREE_GRANT_MS));
      nav("/signal-bot", { replace: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="intro-root">
      <style>{`
        .intro-root { padding:18px 16px 90px; text-align:center; }
        .hero-logo { width:92px; margin:12px auto 6px; }
        .tagline { opacity:.7; margin-bottom:8px; }
        .lucky-remain { color:#38bdf8; font-weight:700; margin-bottom:4px; }

        .enter-btn {
          margin:14px auto 12px;
          padding:11px 0;
          width:68%;
          max-width:260px;
          border-radius:14px;
          border:none;
          background:linear-gradient(135deg,#38bdf8,#3b82f6);
          color:#020617;
          font-size:15px;
          font-weight:800;
          box-shadow:0 6px 20px rgba(56,189,248,.35), inset 0 0 0 1px rgba(255,255,255,.15);
        }
        .enter-btn:active{ transform:scale(.97); }
        .enter-btn:disabled{ opacity:.5; }

        .ticker-grid {
          display:grid;
          grid-template-columns:repeat(2,1fr);
          gap:12px;
          margin-top:10px;
        }

        .ticker-card {
          background:linear-gradient(180deg,#0b1220,#020617);
          border-radius:14px;
          padding:12px 12px 10px;
          text-align:left;
          box-shadow:inset 0 0 0 1px rgba(255,255,255,.05);
        }

        .symbol { font-size:12px; opacity:.6; }
        .price { font-size:18px; font-weight:800; margin:2px 0 6px; }

        .up { color:#4ade80; }
        .down { color:#f87171; }

        .roi-row {
          display:flex;
          justify-content:space-between;
          font-size:12px;
          opacity:.9;
          margin-bottom:2px;
        }

        .spark { width:100%; height:42px; margin-top:6px; }

        .footer-links {
          margin-top:18px;
          display:flex;
          justify-content:center;
          gap:14px;
          font-size:12px;
          opacity:.6;
        }
      `}</style>

      <img src={ZuzuLogo} className="hero-logo" />
      <h1>{t("title")}</h1>
      <p className="tagline">{t("tagline")}</p>

      {remainMin > 0 && (
        <div className="lucky-remain">
          {t("luckyRemain", { min: remainMin })}
        </div>
      )}

      <button className="enter-btn" onClick={enter} disabled={busy}>
        {busy ? t("checking") : t("enter")}
      </button>

      <div className="ticker-grid">
        {SYMBOLS.map((s) => {
          const d = tickers[s];
          if (!d) return null;
          const up = (d.roi24h ?? 0) >= 0;

          return (
            <div key={s} className="ticker-card">
              <div className="symbol">{s.replace("USDT", "")}</div>

              <div className={`price ${up ? "up" : "down"}`}>
                ${d.price?.toLocaleString(undefined,{ minimumFractionDigits:2 })}
              </div>

              <div className="roi-row"><span>1H ROI</span><span className={d.roi1h>=0?"up":"down"}>{d.roi1h?.toFixed(2)}%</span></div>
              <div className="roi-row"><span>4H ROI</span><span className={d.roi4h>=0?"up":"down"}>{d.roi4h?.toFixed(2)}%</span></div>
              <div className="roi-row"><span>24H ROI</span><span className={d.roi24h>=0?"up":"down"}>{d.roi24h?.toFixed(2)}%</span></div>

              <Sparkline data={spark[s] || []} up={up} />
            </div>
          );
        })}
      </div>

      <div className="footer-links">
        <Link to="/terms">{t("links.terms")}</Link>
        <Link to="/privacy">{t("links.privacy")}</Link>
      </div>
    </div>
  );
};

export default SignalIntroPage;
